import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { User } from '../../../core/models/entities/user';
import { AuthService } from '../../../core/services/auth.service';
import { Store } from '@ngrx/store';
import { authActions } from '../../../core/store/auth';
import { ToastService } from '../../../core/services/toast.service';
import { ModalService } from '../../../core/services/modal.service';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { take } from 'rxjs';

@Component({
  selector: 'app-login-form',
  template: `
    <form [formGroup]="form" (ngSubmit)="onSubmit()">
      <app-text-input
        [control]="form.get('email')"
        label="<i class='fal fa-lg fa-paper-plane mr-1.5'></i> Adresse e-mail"
        name="email"
        inputmode="email"
      ></app-text-input>
      <app-password-input
        [control]="form.get('password')"
        label="<i class='fal fa-lg fa-lock mr-1.5'></i> Mot de passe"
        name="password"
      ></app-password-input>

      <div class="flex items-center justify-between">
        <app-single-checkbox-input
          label="Rester connecté"
          [control]="form.get('remember')"
          name="remember"
          [marginBottom]="false"
        ></app-single-checkbox-input>
        <a
          data-cy="forgotten-password-button"
          routerLink="/auth/mot-de-passe-oublie"
          class="text-sm"
          >Mot de passe oublié ?</a
        >
      </div>

      <app-spacer size="6"></app-spacer>

      <div class="text-left">
        <app-button type="submit" [loading]="sendingData">
          Se connecter
        </app-button>
      </div>
    </form>
  `,
  standalone: false,
})
export class LoginFormComponent implements OnInit {
  @Output() afterLogin = new EventEmitter<User>();
  @Input() sendingData: boolean;
  @Input() email: string;
  form: UntypedFormGroup;

  constructor(
    private formValidationService: FormValidationService,
    private authService: AuthService,
    private store: Store,
    private toastService: ToastService,
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {
    this.form = new UntypedFormGroup({
      email: new UntypedFormControl(this.email ?? '', [
        Validators.required,
        Validators.email,
      ]),
      password: new UntypedFormControl('', [Validators.required]),
      remember: new UntypedFormControl(),
    });
  }

  async onSubmit() {
    if (this.formValidationService.validate(this.form)) {
      try {
        this.sendingData = true;
        const response: { user: User; token: string } | boolean =
          await this.authService.login(this.form.value);
        if (response === true) {
          const instance = this.modalService.open(ConfirmModalComponent, {
            title: 'Vérifier votre boîte e-mail',
            message: `Nous vous avons envoyé un lien à ${this.form.value.email}. Merci de cliquer sur ce lien afin d'accéder à votre compte.`,
            okAction: {
              title: 'OK, j’ai compris',
            },
            cancelAction: {
              title: '',
            },
          });
          instance.okClicked$
            .pipe(take(1))
            .subscribe(() => this.modalService.close());
        } else {
          this.store.dispatch(
            authActions.loginSuccess({
              user: (response as { user: User; token: string }).user,
              token: (response as { user: User; token: string }).token,
            }),
          );
          this.afterLogin.emit(
            (response as { user: User; token: string }).user,
          );
        }
      } catch (exception: any) {
        this.toastService.error(exception.message);
      } finally {
        this.sendingData = false;
      }
    }
  }
}
